import React, { useEffect, useState, useRef } from 'react';
import Fuse from 'fuse.js';
import './styles.css';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import InfoPage from "./InfoPage";

export default function App() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hideSuggestions, setHideSuggestions] = useState(false);
  const inputRef = useRef();
  const listRef = useRef();
  const suppressSuggestionsRef = useRef(false);
  const LAST_UPDATED = "2026-05-23";

  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    fetch(base + '/api/search?q=')
      .then((r) => r.json())
      .then((j) => setData(j.results || []))
      .catch(() => setData([]));
  }, []);

  function normalize(s) {
    return (s || '')
      .toString()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  useEffect(() => {
    if (suppressSuggestionsRef.current) {
      suppressSuggestionsRef.current = false;
      return;
    }
    if (hideSuggestions) {
      setSuggestions([]);
      return;
    }

    const q = normalize(query.trim());
    if (!q) {
      setSuggestions([]);
      return;
    }

    const res = data
      .filter((d) => {
        const n = normalize(d.name);
        return n.startsWith(q) || n.includes(q);
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));

    setSuggestions(res);
    setActiveIndex(-1);
  }, [query, data, hideSuggestions]);

  function chooseItem(item) {
    suppressSuggestionsRef.current = true;
    setSelected(item);
    setQuery(item.name);
    setSuggestions([]);
    setHideSuggestions(true);
    setActiveIndex(-1);
    if (inputRef.current) inputRef.current.blur();
  }

  function onKeyDown(e) {
    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length === 1) {
        chooseItem(suggestions[0]);
        return;
      }
      if (activeIndex >= 0) {
        chooseItem(suggestions[activeIndex]);
        return;
      }
    }
  }
  
function phoneToTel(phone) {
  if (!phone) return null;

  const digits = phone.replace(/[^\d]/g, '');

  if (digits.length <= 5) return `tel:${digits}`;
  
  if (digits.startsWith('0')) {
    return `tel:+46${digits.slice(1)}`;
  }

  return `tel:${digits}`;
}

  /* Keep active item in view */
  useEffect(() => {
    const ul = listRef.current;
    if (!ul) return;
    if (activeIndex >= 0 && activeIndex < ul.children.length) {
      ul.children[activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  function onChange(e) {
    setHideSuggestions(false);
    setSelected(null);
    setQuery(e.target.value);
  }

  const clinicLinks = {
    PRIMA: 'https://prima.se/',
    WeMind: 'https://wemind.se/',
    'Psykiatri Nordväst': 'https://www.psykiatrinordvast.se/',
    'Psykiatri Sydväst': 'https://www.psykiatrisydvast.se/',
    'Norra Stockholms Psykiatri': 'https://www.norrastockholmspsykiatri.se/',
    'Psykiatri Södra Stockholm': 'https://www.psykiatrisodrastockholm.se/',
    'Psykiatricentrum Södertälje': 'https://www.psykiatricentrumsodertalje.se/',
    TioHundra: 'https://www.tiohundra.se/vard/psykiatri-och-beroende/'
  };

  function renderClinicLink(name) {
    if (!name) return null;
    const key = Object.keys(clinicLinks).find((k) =>
      name.toLowerCase().includes(k.toLowerCase())
    );

    if (key) {
      return (
        <a className="link" href={clinicLinks[key]} target="_blank" rel="noopener noreferrer">
          {name}
        </a>
      );
    }
    return <span>{name}</span>;
  }

  function renderJourLink(item) {
    if (!item || !item.jourenhet) return null;

    const j = item.jourenhet;

    // --- EXACT MATCH: Rosenlund ---
    if (j.includes('Psykiatri Södra Stockholm (PSS) - Psykiatrisk jourmottagning')) {
      return (
        <span>
          Hemsida:{' '}
          <a
            className="link"
            href="https://www.psykiatrisodrastockholm.se/vard-hos-oss/akut-hjalp/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Psykiatri Södra Stockholm – Psykiatrisk jourmottagning
          </a>
        </span>
      );
    }
    
    const mapping = {
      PRIMA: {
        label: 'PRIMA - Vuxenpsykiatri',
        url: 'https://prima.se/prima-vuxenpsykiatri/kontakta-oss/'
      },
      WeMind: {
        label: 'WeMind - Vuxenpsykiatri',
        url: 'https://wemind.se/vuxenpsykiatri/'
      },
      'Psykiatri Nordväst': {
        label: 'Psykiatri Nordväst – Mobila Akut- och Konsultationsenheten',
        url: 'https://www.psykiatrinordvast.se/vard-hos-oss/akut/'
      },
      'Psykiatri Sydväst': {
        label: 'Psykiatri Sydväst - Jourmottagningen',
        url: 'https://www.psykiatrisydvast.se/vard-hos-oss/akut-hjalp/'
      },
      'Norra Stockholms Psykiatri': {
        label: 'Norra Stockholms Psykiatri – Mobila Akutenheten',
        url: 'https://www.norrastockholmspsykiatri.se/vard-hos-oss/akut-hjalp/'
      },
      'Psykiatricentrum Södertälje': {
        label: 'Psykiatricentrum Södertälje – Utredningsenheten',
        url: 'https://www.psykiatricentrumsodertalje.se/vard-hos-oss/akut-hjalp/'
      },
      TioHundra: {
        label: 'TioHundra – Psykiatri & Beroende',
        url: 'https://www.tiohundra.se/vard/psykiatri-och-beroende/sa-soker-du-hjalp/'
      }
    };

    const found = Object.keys(mapping).find((k) =>
      j.toLowerCase().includes(k.toLowerCase())
    );

    if (found) {
      const m = mapping[found];
      return (
        <span>
          Hemsida:{' '}
          <a className="link" href={m.url} target="_blank" rel="noopener noreferrer">
            {m.label}
          </a>
        </span>
      );
    }

    // fallback
    return null;
  }

  function ContactsBlock() {
    if (!selected) return null;

    return (
      <div id="contacts" style={{ marginTop: 12 }}>
        <div className="card">
          <h3 className="bold">Övriga viktiga kontaktuppgifter</h3>

          <div className="contacts">
            {/* Each link matches the HTML version */}
            <p>
              <strong>Beroendeakuten Stockholm (BAS)</strong><br />
              Öppet dygnet runt.<br />
              Adress: Akutvägen 33, S:t Görans sjukhusområde<br />
              Telefon:{" "}
              <a className="phone-link" href="tel:+46812345900">
              08-123 459 00
              </a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.beroendecentrum.se/vara-mottagningar/akutmottagningar/beroendeakuten/" target="_blank" rel="noopener noreferrer">
                beroendecentrum.se
              </a></p>
            
            <p><strong>BUP Stockholms psykiatriska akutenhet för barn och ungdomar - BUP Akuten</strong><br />
              Öppet dygnet runt.<br />
              Adress: Sachsgatan 10, Södermalm<br />
              Telefon:{" "}
              <a className="phone-link" href="tel:+46812366900">
              08-123 669 00
              </a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.bup.se/kontakta-oss/bup-akuten/" target="_blank" rel="noopener noreferrer">
                bup.se
              </a></p>

            <p><strong>Giftinformationscentralen</strong><br />
              Akuta fall:{" "}
              <a className="phone-link" href="tel:112">112</a><br />
              Rådgivning:{" "}
              <a className="phone-link" href="tel:+46104566700">
              010-456 67 00
              </a><br />
              Hemsida:{' '}
              <a className="link" href="https://giftinformation.se/" target="_blank" rel="noopener noreferrer">
                giftinformation.se
              </a></p>

            <p><strong>Mind Självmordslinjen</strong><br />
              Öppet dygnet runt.<br />
              Telefon:{" "}
              <a className="phone-link" href="tel:90101">
              90101
              </a><br />
              Chatt:{' '}
              <a className="link" href="https://chat.mind.se/" target="_blank" rel="noopener noreferrer">
                chat.mind.se
              </a></p>

            <p><strong>Hjälplinjen</strong><br />
              Telefon:{" "}
              <a className="phone-link" href="tel:90390">
              90390
              </a><br />
              Skriftlig rådgivning:{' '}
              <a className="link" href="https://hjalplinjen.se/skriftlig_radgivning/" target="_blank" rel="noopener noreferrer">
                hjalplinjen.se
              </a></p>

            <p><strong>Jourhavande medmänniska</strong><br />
              Alla dagar kl 21:00-06:00.<br />
              Telefon:{" "}
              <a className="phone-link" href="tel:+4687021680">
              08-702 16 80
              </a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.jourhavande-medmanniska.se/" target="_blank" rel="noopener noreferrer">
                jourhavande-medmanniska.se
              </a></p>

            <p><strong>Jourhavande präst</strong><br />
              Telefon:{" "}
              <a className="phone-link" href="tel:112">112</a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.svenskakyrkan.se/jourhavandeprast/" target="_blank" rel="noopener noreferrer">
                svenskakyrkan.se
              </a></p>

            <p><strong>Jourhavande kompis</strong><br />
              Hemsida:{' '}
              <a className="link" href="https://www.jourhavandekompis.se/" target="_blank" rel="noopener noreferrer">
                jourhavandekompis.se
              </a></p>

            <p><strong>Bris – Barnens rätt i samhället</strong><br />
              Telefon (öppet dygnet runt):{" "}
              <a className="phone-link" href="tel:116111">116 111</a><br />
              Vuxentelefon (vardagar kl 09-12):{" "}
              <a className="phone-link" href="tel:+46771505050">0771-50 50 50</a><br />
              Chatt alla dagar kl 14–21<br />
              Hemsida:{' '}
              <a className="link" href="https://www.bris.se/" target="_blank" rel="noopener noreferrer">
                bris.se
              </a></p>

            <p><strong>Kvinnofridslinjen</strong><br />
              Telefon:{" "}
              <a className="phone-link" href="tel:116016">116 016</a><br />
              Hemsida:{' '}
              <a className="link" href="https://kvinnofridslinjen.se/fa-stod/om-chatten/" target="_blank" rel="noopener noreferrer">
                kvinnofridslinjen.se
              </a></p>

            <p><strong>Maana "Tjejjouren"</strong><br />
              Hemsida:{' '}
              <a className="link" href="https://www.maana.se/" target="_blank" rel="noopener noreferrer">
                maana.se
              </a></p>

            <p><strong>Ätstörningslinjen - Frisk & Fri riksföreningen för ätstörningar</strong><br />
              Telefon:{" "}
              <a className="phone-link" href="tel:020208018">020 – 20 80 18</a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.friskfri.se/fa-stod/" target="_blank" rel="noopener noreferrer">
              friskfri.se
              </a></p>

            <p><strong>Rädda Barnens stödlinje</strong><br />
              Alla dagar kl 15:00-18:00.<br />
              Telefon:{" "}
              <a className="phone-link" href="tel:+46200778820">0200-77 88 20</a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.raddabarnen.se/rad-och-kunskap/barn-och-unga/om-du-behover-hjalp/" target="_blank" rel="noopener noreferrer">
                raddabarnen.se
              </a></p>

            <p><strong>Röda Korsets telefonjour</strong><br />
              Telefon:{" "}
              <a className="phone-link" href="tel:+46771900800">0771-900 800</a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.rodakorset.se/var-hjalp-i-sverige/psykisk-halsa-och-psykisk-ohalsa/" target="_blank" rel="noopener noreferrer">
                rodakorset.se
              </a></p>

            <p><strong>SPES - suicidprevention och efterlevandes stöd</strong><br />
              Alla dagar kl 19:00-22:00.<br />
              Telefon:{" "}
              <a className="phone-link" href="tel:+4620181800">020-18 18 00</a><br />
              Hemsida:{' '}
              <a className="link" href="https://www.spes.se/" target="_blank" rel="noopener noreferrer">
                spes.se
              </a></p>

            <p><strong>Suicide Zero</strong><br />
              Hemsida:{' '}
              <a className="link" href="https://www.suicidezero.se/" target="_blank" rel="noopener noreferrer">
                suicidezero.se
              </a></p>

            <p><strong>Vårdguiden 1177</strong><br />
              Hemsida:{' '}
              <a className="link" href="https://www.1177.se/" target="_blank" rel="noopener noreferrer">
                1177.se
              </a></p>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------
     MAIN RENDER
  ------------------------------------------------------------------------- */

return (
<BrowserRouter> 
  <div className="page">

  <header className="site-header">
    <div className="header-inner">

      <div className="logo">
        <div className="logo-title">
          STHLM<br />PSYK
        </div>
        <div className="logo-subtitle">
          Psykiatri i Stockholm
        </div>
      </div>

    </div>
  </header>

<div className="page-nav">
  <Link className="page-nav-link" to="/">Sök vårdgivare</Link>
  <Link className="page-nav-link" to="/om-psykiatrin">
    Om psykiatri i Stockholm
  </Link>
</div>
    
<Routes>
  <Route path="/" element={
    <div className="container">
    
      <div className="screen-only-intro">
        <h1>Hitta din psykiatriska vårdgivare i Stockholms län</h1>
    
        <label className="bold">
          Sök efter en kommun, stadsdel eller ort för att hitta närmsta psykiatriska vårdgivare och viktiga kontaktupgpifter.
        </label>
      </div>
    
      <input
        ref={inputRef}
        id="search"
        type="text"
        placeholder="Skriv kommun, stadsdel eller ort..."
        autoComplete="off"
        value={query}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />

      {!hideSuggestions && suggestions.length > 0 && (
        <ul id="suggestions" className="suggestions" ref={listRef} role="listbox">
          {suggestions.map((s, idx) => (
            <li
              key={s.name + idx}
              tabIndex={0}
              className={idx === activeIndex ? 'active' : ''}
              onClick={() => chooseItem(s)}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              {s.name}
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <div id="print-area">
          <div className="print-header">
              <h2>{selected.name}</h2>
          </div>
      
          {/* RESULT CARD */}
          <div id="result" className="card" aria-live="polite">
            <div className="note" style={{ marginBottom: '12px' }}>
              <strong>
                Informationen nedan gäller endast personer över 18 år. Barn och ungdomar i Stockholms län hänvisas till Barn- och ungdomspsykiatrin,{" "}
                <a
                  className="link"
                  href="https://www.bup.se/"
                  target="_blank"
                  rel="noopener"
                >
                  BUP
                </a>.
              </strong>
            </div>
      
            <div>
              <strong>Ansvarig klinik för psykiatrisk öppenvård för vuxna:</strong>
              <br />
              {renderClinicLink(selected.oppenvard)}
            </div>
      
            <div style={{ marginTop: 8 }}>
              <strong>Ansvarig klinik för psykiatrisk slutenvård för vuxna:</strong>
              <br />
              {renderClinicLink(selected.slutenvard)}
            </div>
      
            <hr style={{ margin: '12px 0' }} />
      
            <div>
              <strong>Om du mår psykiskt dåligt:</strong>
            </div>
      
            <ol>
              <li>Kontakta din vårdcentral eller ordinarie psykiatriska mottagning.</li>
      
              <li>
                <strong>Kontakta din psykiatriska jourenhet</strong>
                <br />
                <em>{selected.jourenhet}</em>
                <br />
      
                {Array.isArray(selected.jour_phones) && selected.jour_phones.length > 0 ? (
                  <>
                    Telefon:{' '}
                    {selected.jour_phones.map((phone, i) => (
                      <span key={phone}>
                        <a className="phone-link" href={phoneToTel(phone)}>
                          {phone}
                        </a>
                        {i < selected.jour_phones.length - 1 && ' eller '}
                      </span>
                    ))}
                    .
                    {selected.jour_hours && <span> {selected.jour_hours}</span>}
                  </>
                ) : (
                  <>Telefon: Se mottagningens öppettider</>
                )}
      
                <br />
                {renderJourLink(selected)}
              </li>
      
              <li>
                <strong>Kontakta Psykiatriska akutmottagningen, öppet dygnet runt</strong>
                <br />
                Adress: Vårdvägen 5, S:t Görans sjukhusområde, 112 81 Stockholm
                <br />
                Hemsida:{' '}
                <a
                  className="link"
                  href="https://www.norrastockholmspsykiatri.se/vard-hos-oss/akut-hjalp/psykiatriskakutmottagning/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Psykiatriska Akutmottagningen Stockholm
                </a>
              </li>
      
              <li>
                Vid akut nödsituation eller livsfara ring alltid:{' '}
                <a className="phone-link" href="tel:112">112</a>!
              </li>
            </ol>
          </div>
      
          {/* PRINT BUTTON */}
          <div style={{ marginTop: 12 }}>
            <button
              className="print-button"
              onClick={() => window.print()}
            >
              🖨️ Skriv ut
            </button>
          </div>
      
          {/* CONTACTS */}
          <ContactsBlock />
      
          {/* LAST UPDATED + FOOTER */}
          <div className="last-updated">
            Senast uppdaterad: {LAST_UPDATED}
          </div>
      
          <footer id="footer" style={{ marginTop: 12 }}>
            <a
              className="link"
              href="https://vardgivarguiden.se/avtal/avtalsomrade/lou-regionagd/vuxenpsykiatri/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Källa
            </a>{' '}
            — Reservation för felskrivningar - kontrollera alltid med din vårdgivare för aktuella öppettider och telefonnummer. Kontaktinfo: sthlmpsyk (at) gmail.com.
          </footer>
      
        </div>
      )}

        <Analytics />
      </div>
    }
  />
    
      <Route path="/om-psykiatrin" element={<InfoPage />} />
    </Routes>
    
    <footer className="site-footer" />
    </div>
  </BrowserRouter>
);
}
